import { useState } from 'react'
import {
  Check,
  Minus,
  ArrowUpRight,
  Users,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocale } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'
import { useApi } from '../hooks/useApi'
import { pricingApi, helpApi } from '../lib/api'
import { PageHeading, ServiceState, InnerClosing } from '../components/Interior'

const principleIcons = [ShieldCheck, SlidersHorizontal, Users]

export default function Pricing() {
  const { t } = useTranslation(['pricing', 'common'])
  const { htmlLang } = useLocale()
  const [yearly, setYearly] = useState(false)
  const {
    data: plans,
    loading,
    error,
    refetch,
  } = useApi(() => pricingApi.getAll(), [])
  const { data: faqs } = useApi(() => helpApi.getFAQ('pricing'), [])
  const principles = t('principles', { returnObjects: true }) as {
    title: string
    text: string
  }[]
  return (
    <div className="designed-page pricing-page">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/pricing"
        structuredData={[schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.pricing'), url: '/pricing' }])]}
      />
      <PageHeading
        label={t('heading.label')}
        title={t('heading.title')}
        accent={t('heading.accent')}
        description={t('heading.description')}
      >
        <div className="plan-sculpture" aria-hidden="true">
          <div>
            <span>01</span>
            <Users size={22} />
            <b>{t('sculpture.you')}</b>
          </div>
          <div>
            <span>02</span>
            <Users size={28} />
            <b>{t('sculpture.team')}</b>
          </div>
          <div>
            <span>03</span>
            <Users size={35} />
            <b>{t('sculpture.possibilities')}</b>
          </div>
        </div>
      </PageHeading>
      <div className="pricing-toolbar">
        <div>
          <h2>{t('toolbar.title')}</h2>
          <p>{t('toolbar.text')}</p>
        </div>
        <div className="billing-switch" aria-label={t('toolbar.billingLabel')}>
          <button aria-pressed={!yearly} onClick={() => setYearly(false)}>
            {t('toolbar.monthly')}
          </button>
          <button aria-pressed={yearly} onClick={() => setYearly(true)}>
            {t('toolbar.yearly')}
          </button>
        </div>
      </div>
      <ServiceState
        loading={loading}
        error={error}
        empty={!plans?.length}
        retry={refetch}
      />
      {!loading && !error && !!plans?.length && (
        <div className="plan-grid">
          {plans.map((plan, index) => {
            const price = yearly ? plan.yearlyPrice : plan.monthlyPrice
            const saving =
              plan.monthlyPrice > 0
                ? Math.round(
                    (1 - plan.yearlyPrice / (plan.monthlyPrice * 12)) * 100,
                  )
                : 0
            let formatted: string
            try {
              formatted = new Intl.NumberFormat(htmlLang, {
                style: 'currency',
                currency: plan.currency || 'USD',
                maximumFractionDigits: 2,
              }).format(price)
            } catch {
              formatted = price + ' ' + plan.currency
            }
            return (
              <article
                className={
                  plan.popular ? 'plan-card featured-plan' : 'plan-card'
                }
                key={plan.id}
              >
                <div className="plan-card-top">
                  <span>{t('plan.index', { n: index + 1 })}</span>
                  {plan.popular && <b>{t('plan.featured')}</b>}
                </div>
                <h3>{plan.name}</h3>
                <p>{plan.description}</p>
                <div className="plan-price">
                  {formatted}
                  <span>/{yearly ? t('plan.perYear') : t('plan.perMonth')}</span>
                </div>
                <div className="plan-saving">
                  {yearly && saving > 0
                    ? t('plan.saving', { percent: saving })
                    : t('plan.total')}
                </div>
                <Link
                  className={plan.popular ? 'jade-button' : 'outline-button'}
                  to={price === 0 ? '/download' : '/contact'}
                >
                  {price === 0 ? t('plan.startFree') : t('plan.discuss')}
                  <ArrowUpRight size={17} />
                </Link>
                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature.id}>
                      {feature.included ? (
                        <Check size={16} />
                      ) : (
                        <Minus size={16} />
                      )}
                      <span>
                        {feature.text}
                        {!feature.included && <small>{t('plan.notIncluded')}</small>}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            )
          })}
        </div>
      )}
      <div className="pricing-principles">
        {principles.map((principle, index) => {
          const Icon = principleIcons[index]
          return (
            <article key={index}>
              <Icon />
              <h3>{principle.title}</h3>
              <p>{principle.text}</p>
            </article>
          )
        })}
      </div>
      {!!faqs?.length && (
        <section className="interior-faq">
          <div>
            <span className="eyebrow">{t('faq.label')}</span>
            <h2>
              {t('faq.title')}
              <br />
              {t('faq.titleLine2')}
            </h2>
          </div>
          <div>
            {faqs.map((faq) => (
              <details key={faq.id}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}
      <InnerClosing
        title={t('closing.title')}
        text={t('closing.text')}
      />
    </div>
  )
}
